import { X } from "lucide-react";
import { useEffect, type JSX, type ReactNode } from "react";

import { cn } from "@/shared/lib/cn";

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  width?: number;
  children: ReactNode;
  "data-testid"?: string;
}

/** Right-anchored drawer (480px default). Scrim closes; Esc closes; page beneath stays mounted. */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  actions,
  width = 480,
  children,
  "data-testid": testId,
}: DrawerProps): JSX.Element {
  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  return (
    <>
      <button
        type="button"
        aria-label="Close"
        tabIndex={open ? 0 : -1}
        onClick={onClose}
        className={cn(
          "fixed inset-0 z-50 cursor-default transition-opacity duration-300",
          // `hidden` removes the scrim from layout/hit-testing entirely when closed —
          // `pointer-events-none` alone still leaves it covering the page.
          open ? "opacity-100" : "hidden pointer-events-none opacity-0",
        )}
        style={{ background: "rgba(16,24,40,.28)", backdropFilter: "blur(2px)" }}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
        data-testid={testId}
        className={cn(
          "fixed inset-y-0 right-0 z-50 flex h-dvh flex-col bg-white transition-transform duration-300",
          // When closed the panel sits at translate-x-full (off-screen right). A fixed
          // element with a transform still occupies layout width, so the off-screen panel
          // extends document scrollWidth and reappears on any horizontal scroll/pan.
          // `hidden` (display:none) takes it out of layout flow so it can never overflow.
          open ? "translate-x-0" : "hidden translate-x-full",
        )}
        style={{ width, maxWidth: "92vw", boxShadow: "var(--sh-modal)" }}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[var(--premium-border)] px-5 py-4">
          <div className="min-w-0">
            {title && <div className="text-base font-bold">{title}</div>}
            {subtitle && (
              <div className="mt-0.5 text-[12.5px] text-[var(--text-subtle)]">{subtitle}</div>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {actions}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="grid h-8 w-8 place-items-center rounded-lg text-[var(--text-subtle)] transition hover:bg-[var(--canvas)] focus-ring"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </aside>
    </>
  );
}
